// src/screens/Championship/PenaltyTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import teamService from '../../services/teamService';
import penaltyService from '../../services/penaltyService';
import './PenaltyTab.css';

// Tipos de sanção
const SANCTION_TYPES = {
  SUSPENSION: 'suspension',
  POINTS: 'points',
};

const PenaltyTab = ({ championshipId }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Estado para controlar quais times estão com o elenco visível
  const [expandedTeams, setExpandedTeams] = useState({});

  // Modal state
  const [modal, setModal] = useState({ open: false, target: null, targetType: null });

  // Form state
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [banRoster, setBanRoster] = useState(false);
  
  // 🟢 NOVOS ESTADOS PARA PERDA DE PONTOS
  const [sanctionType, setSanctionType] = useState(SANCTION_TYPES.SUSPENSION);
  const [pointsToDeduct, setPointsToDeduct] = useState('');

  const showFeedback = (msg, isError = false) => {
    if (isError) { 
      setError(msg); 
      setSuccessMsg(null); 
    } else { 
      setSuccessMsg(msg); 
      setError(null); 
    }
    setTimeout(() => { 
      setError(null); 
      setSuccessMsg(null); 
    }, 4000);
  };

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const populatedTeams = await teamService.getTeamsByChampionship(championshipId) || [];
      setTeams(populatedTeams);
    } catch (err) {
      console.error("Erro ao carregar tribunal:", err);
      setError('Erro ao carregar times do campeonato.');
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  // Alterna a visibilidade do elenco de um time
  const toggleTeamPlayers = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const openModal = (target, targetType) => {
    setModal({ open: true, target, targetType });
    setReason('');
    setExpiryDate('');
    setPermanent(false);
    setBanRoster(false);
    setSanctionType(SANCTION_TYPES.SUSPENSION);
    setPointsToDeduct('');
  };

  const closeModal = () => {
    setModal({ open: false, target: null, targetType: null });
  };

  const handleApplyPenalty = async () => {
    if (!reason.trim()) {
      showFeedback('Informe o motivo da punição.', true);
      return;
    }

    // 🟢 Validação para perda de pontos
    if (sanctionType === SANCTION_TYPES.POINTS) {
      const points = parseInt(pointsToDeduct);
      if (!points || points <= 0) {
        showFeedback('Informe uma quantidade válida de pontos a deduzir (mínimo 1).', true);
        return;
      }
      if (modal.targetType !== 'team') {
        showFeedback('Perda de pontos só pode ser aplicada a times.', true);
        return;
      }
    }

    try {
      if (sanctionType === SANCTION_TYPES.POINTS && modal.targetType === 'team') {
        // 🟢 APLICA PERDA DE PONTOS NA TABELA
        await penaltyService.applyPointDeduction({
          championshipId,
          teamId: modal.target._id,
          points: parseInt(pointsToDeduct),
          reason,
        });
        
        showFeedback(`Perda de ${pointsToDeduct} ponto(s) aplicada a ${modal.target.name}`);
      } else {
        // Aplica suspensão (comportamento original)
        const payload = {
          reason,
          expiryDate: permanent ? null : expiryDate || null,
        };

        if (modal.targetType === 'player') {
          await penaltyService.applyPlayerPenalty({ ...payload, playerId: modal.target._id });
        } else {
          await penaltyService.applyTeamPenalty({ ...payload, teamId: modal.target._id, banRoster });
        }

        showFeedback(`Punição aplicada a ${modal.target?.name || modal.target?.nickname}`);
      }

      closeModal();
      loadTeams();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Erro ao aplicar punição.';
      showFeedback(msg, true);
    }
  };

  const handleRemovePenalty = (targetType, targetId) => {
    if (!window.confirm('Deseja anular a suspensão deste registro?')) return;
    
    (async () => {
      try {
        await penaltyService.removePenalty(targetType, targetId);
        showFeedback('Punição removida com sucesso.');
        loadTeams();
      } catch (err) {
        showFeedback(err?.response?.data?.error || 'Erro ao remover punição.', true);
      }
    })();
  };

  const isBannedActive = (penalty) => {
    if (!penalty?.isBanned) return false;
    if (!penalty.expiryDate) return true;
    return new Date(penalty.expiryDate) > new Date();
  };

  if (loading) return <div className="pt-loading">Carregando...</div>;

  return (
    <div className="pt-container">
      <div className="pt-header">
        <span className="pt-icon">🔨</span>
        <div>
          <h2 className="pt-title">Tribunal Disciplinar</h2>
          <p className="pt-subtitle">Gerencie suspensões e perda de pontos do sistema</p>
        </div>
      </div>

      {error && <div className="pt-alert pt-alert--error">{error}</div>}
      {successMsg && <div className="pt-alert pt-alert--success">{successMsg}</div>}

      {teams.length === 0 ? (
        <div className="pt-empty">Nenhum time inscrito neste campeonato.</div>
      ) : (
        teams.map((team) => (
          <div key={team._id} className={`pt-team ${isBannedActive(team.penalty) ? 'pt-team--banned' : ''}`}>
            {/* Cabeçalho do time */}
            <div className="pt-team-header">
              <div className="pt-team-info">
                {team.badge && <img src={team.badge} alt={team.name} className="pt-team-badge" />}
                <div>
                  <span className="pt-team-name">{team.name}</span>
                  <span className="pt-team-city">{team.city}</span>
                  {/* 🟢 Exibe se o time tem perda de pontos */}
                  {team.pointDeduction && team.pointDeduction > 0 && (
                    <div className="pt-point-deduction-badge">
                      ⚠️ -{team.pointDeduction} pts (Tribunal)
                    </div>
                  )}
                </div>
              </div>
              <div className="pt-team-actions">
                {isBannedActive(team.penalty) ? (
                  <>
                    <span className="pt-badge pt-badge--banned">
                      🚫 Banido{team.penalty?.expiryDate ? ` até ${new Date(team.penalty.expiryDate).toLocaleDateString('pt-BR')}` : ' (permanente)'}
                    </span>
                    <button className="pt-btn pt-btn--remove" onClick={() => handleRemovePenalty('team', team._id)}>
                      Remover punição
                    </button>
                  </>
                ) : (
                  <button className="pt-btn pt-btn--ban" onClick={() => openModal(team, 'team')}>
                    🔨 Punir time
                  </button>
                )}
              </div>
            </div>

            {/* Motivo atual */}
            {isBannedActive(team.penalty) && team.penalty?.reason && (
              <div className="pt-reason">
                <strong>Motivo:</strong> {team.penalty.reason}
              </div>
            )}

            {/* Botão para mostrar/ocultar elenco */}
            {team.players && team.players.length > 0 && (
              <div className="pt-toggle-players">
                <button
                  className="pt-btn pt-btn--toggle"
                  onClick={() => toggleTeamPlayers(team._id)}
                >
                  {expandedTeams[team._id] ? '🔼 Ocultar jogadores' : `🔽 Mostrar jogadores (${team.players.length})`}
                </button>
              </div>
            )}

            {/* Lista de jogadores (somente se expandida) */}
            {expandedTeams[team._id] && team.players && team.players.length > 0 && (
              <div className="pt-players">
                <p className="pt-players-title">Jogadores</p>
                <div className="pt-players-list">
                  {team.players.map((player) => (
                    <div key={player._id} className={`pt-player ${isBannedActive(player.penalty) ? 'pt-player--banned' : ''}`}>
                      <div className="pt-player-info">
                        {player.photo && <img src={player.photo} alt={player.name} className="pt-player-photo" />}
                        <div>
                          <span className="pt-player-name">{player.nickname || player.name}</span>
                          {isBannedActive(player.penalty) && (
                            <span className="pt-badge pt-badge--banned pt-badge--sm">
                              🚫 {player.penalty?.expiryDate ? `até ${new Date(player.penalty.expiryDate).toLocaleDateString('pt-BR')}` : 'permanente'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="pt-player-actions">
                        {isBannedActive(player.penalty) ? (
                          <button className="pt-btn pt-btn--remove pt-btn--sm" onClick={() => handleRemovePenalty('player', player._id)}>
                            Remover
                          </button>
                        ) : (
                          <button className="pt-btn pt-btn--ban pt-btn--sm" onClick={() => openModal(player, 'player')}>
                            Punir
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Caso não haja jogadores */}
            {(!team.players || team.players.length === 0) && (
              <div className="pt-no-players">Nenhum jogador cadastrado neste time.</div>
            )}
          </div>
        ))
      )}

      {/* 🟢 MODAL DE PUNIÇÃO ATUALIZADO */}
      {modal.open && (
        <div className="pt-modal-overlay" onClick={closeModal}>
          <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="pt-modal-title">
              🔨 Punir {modal.targetType === 'player' ? 'Jogador' : 'Time'}: <strong>{modal.target?.nickname || modal.target?.name}</strong>
            </h3>

            {/* 🟢 TIPO DE SANÇÃO */}
            <div className="pt-form-group">
              <label className="pt-label">Tipo de Sanção:</label>
              <div className="pt-sanction-type-row">
                <button
                  className={`pt-sanction-type-btn ${sanctionType === SANCTION_TYPES.SUSPENSION ? 'pt-sanction-type-active' : ''}`}
                  onClick={() => setSanctionType(SANCTION_TYPES.SUSPENSION)}
                >
                  <span className={`pt-sanction-type-text ${sanctionType === SANCTION_TYPES.SUSPENSION ? 'pt-sanction-type-text-active' : ''}`}>
                    🚫 Suspensão
                  </span>
                </button>
                
                <button
                  className={`pt-sanction-type-btn ${sanctionType === SANCTION_TYPES.POINTS ? 'pt-sanction-type-active' : ''} ${modal.targetType !== 'team' ? 'pt-sanction-type-disabled' : ''}`}
                  onClick={() => {
                    if (modal.targetType === 'team') {
                      setSanctionType(SANCTION_TYPES.POINTS);
                    } else {
                      showFeedback('Perda de pontos só pode ser aplicada a times.', true);
                    }
                  }}
                  disabled={modal.targetType !== 'team'}
                >
                  <span className={`pt-sanction-type-text ${sanctionType === SANCTION_TYPES.POINTS ? 'pt-sanction-type-text-active' : ''} ${modal.targetType !== 'team' ? 'pt-sanction-type-text-disabled' : ''}`}>
                    📉 Perda de Pontos
                  </span>
                </button>
              </div>
            </div>

            {/* 🟢 CAMPOS PARA PERDA DE PONTOS */}
            {sanctionType === SANCTION_TYPES.POINTS && modal.targetType === 'team' && (
              <div className="pt-form-group">
                <label className="pt-label">Quantidade de Pontos a Deduzir:</label>
                <input
                  type="number"
                  className="pt-input"
                  placeholder="Ex: 3"
                  value={pointsToDeduct}
                  onChange={(e) => setPointsToDeduct(e.target.value)}
                  min="1"
                />
                <small className="pt-hint-text">
                  A dedução será aplicada imediatamente na tabela de classificação.
                </small>
              </div>
            )}

            {/* 🟢 CAMPOS PARA SUSPENSÃO */}
            {sanctionType === SANCTION_TYPES.SUSPENSION && (
              <>
                <label className="pt-label">Motivo da suspensão *</label>
                <textarea
                  className="pt-textarea"
                  placeholder="Descreva o motivo da punição..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />

                <div className="pt-checkbox-row">
                  <input
                    type="checkbox"
                    id="permanent"
                    checked={permanent}
                    onChange={(e) => setPermanent(e.target.checked)}
                  />
                  <label htmlFor="permanent">Suspensão permanente</label>
                </div>

                {!permanent && (
                  <input
                    type="date"
                    className="pt-input"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                )}

                {/* Opção de punir elenco (apenas para times) */}
                {modal.targetType === 'team' && (
                  <div className="pt-checkbox-row pt-alert-box">
                    <input
                      type="checkbox"
                      id="banRoster"
                      checked={banRoster}
                      onChange={(e) => setBanRoster(e.target.checked)}
                    />
                    <label htmlFor="banRoster" style={{ color: '#f44336', fontWeight: 'bold' }}>
                      🔨 Estender punição a todos os jogadores do elenco?
                    </label>
                  </div>
                )}
              </>
            )}

            {/* 🟢 CAMPO DE MOTIVO PARA PERDA DE PONTOS */}
            {sanctionType === SANCTION_TYPES.POINTS && modal.targetType === 'team' && (
              <>
                <label className="pt-label">Motivo da perda de pontos *</label>
                <textarea
                  className="pt-textarea"
                  placeholder="Descreva o motivo da perda de pontos..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </>
            )}

            <div className="pt-modal-actions">
              <button className="pt-btn pt-btn--cancel" onClick={closeModal}>Cancelar</button>
              <button className="pt-btn pt-btn--confirm" onClick={handleApplyPenalty}>
                {sanctionType === SANCTION_TYPES.POINTS ? 'CONFIRMAR PERDA' : 'Confirmar punição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PenaltyTab;